/**
 * Dealer Desk trade tools (DEALER_ESTIMATE_PLAN.md, approved 2026-10-18), shared by the open / edit
 * position pop-ups:
 *
 * - InstrumentPicker: the instrument as a dropdown with logos, search and asset-class filters.
 * - DeskChart: live candles with the Sell / Buy prices and the trade's lines (entry, TP, SL, stop-out);
 *   TP / SL drag to set the field. With `range` it shows a closed trade's life from price history.
 *   With `pick` ("From chart", CHART_ENTRY_PLAN.md) a click on a candle picks a price and minute;
 *   earlier and later history loads as the dealer scrolls, and "Go to date" jumps to a day.
 *   With `accountId` it marks when the client themselves was online (a bubble where each stretch
 *   starts, a band for how long; desk/online.ts clientOnline), never a staff member logged in as them.
 *   On a master account (`followers`) the clients are its followers, each stretch named.
 * - The estimate panels: what the trade is worth with its leverage. Same trading maths as the
 *   client's order panel (@platform/shared), so the dealer and the client see the same figures.
 */
import { useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { CandlestickSeries, ColorType, createChart, createSeriesMarkers, CrosshairMode, LineStyle, type IChartApi, type IPriceLine, type ISeriesApi, type ISeriesMarkersPluginApi, type Time, type UTCTimestamp } from 'lightweight-charts';
import {
  closePrice as exitPrice,
  D,
  feeFor,
  fmtUsd,
  initialMargin,
  liquidationPrice,
  netUnits,
  notionalOf,
  openFeeFor,
  pipSize,
  pnl,
  searchInstruments,
  TF_SECONDS,
  usdPerPoint,
  type AssetClass,
  type Candle,
  type Instrument,
  type Side,
  type Timeframe,
} from '@platform/shared';
import { InstrumentIcon, InstrumentLabel } from '../components/InstrumentIcon';
import { PairName } from '../components/PairName';
import { cx } from '../components/ui';
import { api } from '../lib/api';
import { cssVar, useTheme } from '../lib/theme';
import { useChannel } from '../lib/ws';
import { useLoad } from '../stores/account';
import { useInstruments, useTickers, type LiveQuote } from '../stores/market';

const tone = (x: D | null | undefined) => (!x || x.isZero() ? '' : x.isNeg() ? 'text-down' : 'text-up');
const signedUsd = (x: D) => `${x.isNeg() ? '−' : '+'}${fmtUsd(x.abs())}`;
const signedPct = (x: D | null) => (x == null ? '—' : `${x.isNeg() ? '−' : '+'}${x.abs().toFixed(1)}%`);
const ofMargin = (x: D, margin: D) => (margin.isPos() ? x.div(margin).mul(100) : null);

// ---------------------------------------------------------------- instrument picker

const FILTERS: { id: AssetClass | 'ALL'; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'FX', label: 'Forex' },
  { id: 'CRYPTO', label: 'Crypto' },
  { id: 'COMMODITY', label: 'Commodities' },
  { id: 'INDEX', label: 'Indices' },
  { id: 'STOCK', label: 'Stocks' },
];

/** The instrument as a dropdown: logo, pair and price on the button; search, filters and one row per market in the list. */
export function InstrumentPicker({ value, onChange }: { value: string; onChange: (symbol: string) => void }) {
  const instruments = useInstruments();
  const tickers = useTickers();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<AssetClass | 'ALL'>('ALL');
  const [sel, setSel] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const current = instruments.find((i) => i.symbol === value) ?? null;

  const rows = useMemo(() => {
    const list = instruments.filter((i) => i.enabled !== false && (filter === 'ALL' || i.assetClass === filter));
    return (query.trim() ? searchInstruments(list, query).map((h) => h.item) : [...list].sort((a, b) => a.symbol.localeCompare(b.symbol))).slice(0, 200);
  }, [instruments, query, filter]);

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const off = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);

  const pick = (i: Instrument | undefined) => {
    if (!i) return;
    onChange(i.symbol);
    setOpen(false);
    setQuery('');
  };
  const t = current ? tickers[current.symbol] : undefined;
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Instrument"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center gap-2 rounded-lg border border-line bg-bg px-2 text-left text-[13px] text-fg hover:border-line-strong"
      >
        {current ? (
          <>
            <InstrumentIcon symbol={current.symbol} size={22} />
            <span className="min-w-0 flex-1 truncate">
              <PairName symbol={current.symbol} assetClass={current.assetClass} tag={false} className="font-semibold" />
              <span className="ml-1.5 text-[11px] text-muted">{current.name}</span>
            </span>
            {t && <span className="num text-[11px] text-muted">{D.from(t.bid).toFixed(current.priceDecimals)}</span>}
          </>
        ) : (
          <span className="flex-1 text-muted">Choose an instrument</span>
        )}
        <span className="text-muted" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute top-10 left-0 z-50 flex max-h-[360px] w-[380px] max-w-[90vw] flex-col rounded-xl border border-line-strong bg-panel p-2 shadow-2xl">
          <input
            ref={input}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') (e.preventDefault(), setSel((s) => Math.min(s + 1, rows.length - 1)));
              else if (e.key === 'ArrowUp') (e.preventDefault(), setSel((s) => Math.max(s - 1, 0)));
              else if (e.key === 'Enter') (e.preventDefault(), pick(rows[sel]));
              else if (e.key === 'Escape') setOpen(false);
            }}
            placeholder="Search, e.g. eurusd, gold, nasdaq"
            aria-label="Search instruments"
            role="combobox"
            aria-expanded="true"
            aria-controls="desk-instrument-list"
            autoComplete="off"
            spellCheck={false}
            className="h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none placeholder:text-muted focus:border-line-strong"
          />
          <div role="group" aria-label="Asset class" className="no-scrollbar flex shrink-0 gap-1 overflow-x-auto py-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={filter === f.id}
                onClick={() => (setFilter(f.id), setSel(0), input.current?.focus())}
                className={cx('h-[26px] shrink-0 rounded-full border px-2.5 text-xs font-medium', filter === f.id ? 'border-line-strong bg-raised text-fg' : 'border-line text-muted hover:text-fg')}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div id="desk-instrument-list" role="listbox" aria-label="Instruments" className="min-h-0 flex-1 overflow-y-auto">
            {rows.length === 0 && <p className="m-0 p-3 text-xs text-muted">No instrument matches “{query}”.</p>}
            {rows.map((i, n) => {
              const tk = tickers[i.symbol];
              const chg = tk?.chg24 == null ? null : D.from(tk.chg24);
              return (
                <button
                  key={i.symbol}
                  type="button"
                  role="option"
                  aria-selected={i.symbol === value}
                  onMouseEnter={() => setSel(n)}
                  onClick={() => pick(i)}
                  className={cx('grid w-full grid-cols-[24px_minmax(0,1fr)_auto_52px] items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px]', n === sel ? 'bg-raised' : '')}
                >
                  <InstrumentIcon symbol={i.symbol} size={22} />
                  <span className="min-w-0 truncate">
                    <PairName symbol={i.symbol} assetClass={i.assetClass} tag={false} className="font-semibold text-fg" />
                    <span className="ml-1.5 text-[11px] text-muted">{i.name}</span>
                  </span>
                  <span className="num text-fg">{tk ? D.from(tk.bid).toFixed(i.priceDecimals) : '—'}</span>
                  <span className={cx('num text-right', tone(chg))}>{chg == null ? '' : `${chg.isNeg() ? '' : '+'}${chg.toFixed(2)}%`}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- chart

export type LineTone = 'up' | 'down' | 'accent' | 'warn' | 'muted';
export interface DeskLine {
  id: string;
  price: string | null;
  label: string;
  tone: LineTone;
  dashed?: boolean;
  /** TP / SL lines the dealer may drag to set the field. */
  drag?: boolean;
}
export interface DeskMark {
  /** ms */
  t: number;
  label: string;
  tone: LineTone;
  below?: boolean;
}

const TONE_VAR: Record<LineTone, string> = { up: '--green', down: '--red', accent: '--accent', warn: '--orange', muted: '--muted' };
const LIVE_TFS: Timeframe[] = ['1m', '5m', '15m', '1h', '4h', '1d'];
const sec = (ms: number) => Math.floor(ms / 1000) as UTCTimestamp;

const UTC_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const LONDON_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short' });
/** "Tue 22 Sep 2026, 09:14 UTC" */
export const utcLabel = (ms: number) => `${UTC_FMT.format(ms)} UTC`;
/** London's wall-clock time at that moment, BST or GMT as it was then: "10:14 BST". */
export const londonLabel = (ms: number) => LONDON_FMT.format(ms).replace(/\s+GMT\+1$/, ' BST');

/** desk/online.ts clientOnline: the client's own online stretches (a master account's: its followers', each with `who`). */
interface ClientOnline {
  historyFrom: string;
  online: boolean;
  master: { followers: number } | null;
  stretches: { from: string; to: string | null; pages: string[]; device: string | null; who?: { userId: string; name: string } }[];
  signIns: { at: string; device: string; who?: { userId: string; name: string } }[];
}
const PAGE: Record<string, string> = { trade: 'Trade', wallet: 'Wallet', deposit: 'Deposit', withdraw: 'Withdraw', kyc: 'KYC', support: 'Support', portfolio: 'Portfolio', account: 'Account', other: 'Other page' };
const ONLINE_PREF = 'deskChartClientOnline';
const readOnlinePref = () => {
  try {
    return localStorage.getItem(ONLINE_PREF) !== 'off';
  } catch {
    return true;
  }
};
/** The online history the chart asks for at most (it is kept 180 days). */
const ONLINE_REACH_MS = 179 * 86_400_000;
const HM_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const DAY_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
/** "47 min", "2 h 05 min", "3 d 4 h" */
export function spanLabel(ms: number): string {
  const m = Math.max(1, Math.round(ms / 60_000));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${String(m % 60).padStart(2, '0')} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}
/** The bubble's own short label: "47m", "2h", "3d". */
const shortSpan = (ms: number) => {
  const m = Math.max(1, Math.round(ms / 60_000));
  return m < 60 ? `${m}m` : m < 48 * 60 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;
};
interface Visit {
  from: number;
  /** null while still online */
  to: number | null;
  pages: string[];
  device: string | null;
  /** A master's follower: who was online. */
  who?: string;
}
/**
 * For each mark (Opened, Closed, Entry) at or after the history began: whether the client was online
 * at that moment, else how long before they had last been online. `who`: the followers online then (a master).
 */
export function onlineAtMarks(marks: DeskMark[], visits: Visit[], historyFrom: number, now = Date.now()) {
  return marks
    .filter((m) => m.t <= now)
    .map((m) => {
      if (m.t < historyFrom) return { label: m.label, t: m.t, state: 'unknown' as const, ago: null, who: [] as string[] };
      const on = visits.filter((v) => v.from <= m.t && (v.to == null || v.to >= m.t));
      if (on.length) return { label: m.label, t: m.t, state: 'online' as const, ago: null, who: [...new Set(on.map((v) => v.who).filter((x): x is string => !!x))] };
      const last = visits.filter((v) => v.to != null && v.to < m.t).reduce<number | null>((x, v) => Math.max(x ?? 0, v.to!), null);
      return { label: m.label, t: m.t, state: 'offline' as const, ago: last == null ? null : m.t - last, who: [] as string[] };
    });
}

/** "From chart": the picked minute, and what a click on a candle reports. */
export interface DeskPick {
  /** ms; the chart scrolls to it when it is outside the candles loaded */
  at: number | null;
  /** The candle's start (ms), the price under the pointer kept inside the candle's low – high, and the candle's length in minutes. */
  onPick: (at: number, price: string, minutes: number) => void;
}
interface Hover {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
}

/** The timeframe that shows a trade's whole life in at most ~1,000 candles. */
export function tfForSpan(ms: number): Timeframe {
  const h = ms / 3_600_000;
  return h <= 16 ? '1m' : h <= 80 ? '5m' : h <= 240 ? '15m' : h <= 960 ? '1h' : h <= 3600 ? '4h' : '1d';
}

/**
 * Live candles of `symbol` (or, with `range`, its candles over that time) with the trade's lines and
 * marks. `onDrag` receives a draggable line's new price as the dealer moves it.
 */
export function DeskChart({
  symbol,
  dp,
  lines,
  marks = [],
  range,
  defaultTf = '5m',
  quote,
  height = 250,
  onDrag,
  onCandles,
  pick,
  accountId,
  followers = false,
}: {
  symbol: string;
  dp: number;
  lines: DeskLine[];
  marks?: DeskMark[];
  range?: { from: number; to: number };
  defaultTf?: Timeframe;
  quote?: LiveQuote | null;
  height?: number;
  onDrag?: (id: string, price: string) => void;
  /** The candles shown (history: best and worst point of a closed trade). */
  onCandles?: (c: Candle[]) => void;
  /** Pick mode: a click on a candle sets the entry. */
  pick?: DeskPick;
  /** The client's account: mark when the client was online. */
  accountId?: string;
  /** A master account: the clients online are its followers. */
  followers?: boolean;
}) {
  const theme = useTheme();
  const box = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const markers = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const drawn = useRef<{ l: DeskLine; line: IPriceLine }[]>([]);
  const drag = useRef<{ id: string; moved: boolean } | null>(null);
  /** Prices the scale must show besides the candles: the trade's own lines (not the far-off stop-out). */
  const fit = useRef<number[]>([]);
  const [tf, setTf] = useState<Timeframe>(range ? tfForSpan(range.to - range.from) : defaultTf);
  /** False once the dealer picks a timeframe: then a short history is shown as it is, not stepped up. */
  const auto = useRef(true);
  /** Pick mode: the moment the loaded candles are centred on (Go to date, or a typed entry time); null = up to now. */
  const [focus, setFocus] = useState<number | null>(null);
  const pickRef = useRef(pick);
  pickRef.current = pick;
  const pickTf = (x: Timeframe) => {
    auto.current = false;
    // Keep the picked entry in view at the new timeframe.
    if (pick?.at != null) setFocus(pick.at);
    setTf(x);
  };
  /** The candles on the chart, oldest first, and whether more lie before / after them (pick mode loads them on scroll). */
  const bars = useRef<Candle[]>([]);
  const edges = useRef({ older: true, newer: false, busy: false });
  const cur = useRef({ symbol, tf });
  cur.current = { symbol, tf };
  /** A drag of a line ends in a click; that click is not a pick. */
  const dragged = useRef(false);
  const [hover, setHover] = useState<Hover | null>(null);
  const [loaded, setLoaded] = useState<'loading' | 'ok' | 'empty'>('loading');
  const [cursor, setCursor] = useState('');
  const live = !range;
  // Client online: the stretches, whether they are shown, the first candle loaded (what to ask for),
  // and a redraw on every scroll, zoom or resize (the bands follow the candles).
  const [online, setOnline] = useState<ClientOnline | null>(null);
  const [showOnline, setShowOnline] = useState(readOnlinePref);
  const [firstBar, setFirstBar] = useState<number | null>(null);
  const [, setFrame] = useState(0);
  const frame = useRef(0);
  const redraw = () => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      setFrame((n) => n + 1);
    });
  };
  const [tip, setTip] = useState<{ x: number; visits: Visit[] } | null>(null);

  useEffect(() => {
    if (!box.current) return;
    const c = createChart(box.current, { autoSize: true, crosshair: { mode: CrosshairMode.Normal }, timeScale: { timeVisible: true, secondsVisible: false } });
    c.priceScale('right').applyOptions({ scaleMargins: { top: 0.08, bottom: 0.08 } });
    const s = c.addSeries(CandlestickSeries, {
      priceFormat: { type: 'price', precision: dp, minMove: 1 / 10 ** dp },
      priceLineVisible: false,
      lastValueVisible: !live,
      autoscaleInfoProvider: (base: () => { priceRange: { minValue: number; maxValue: number } | null } | null) => {
        const r = base();
        if (!r?.priceRange || !fit.current.length) return r;
        // Only lines near the market widen the scale: a mistyped TP far away (5,646,464 on a 100 price)
        // would flatten the candles into one line. Such a line stays off-screen until it is fixed.
        const { minValue: lo, maxValue: hi } = r.priceRange;
        const reach = Math.max(hi - lo, hi * 0.02) * 3;
        const near = fit.current.filter((v) => v >= lo - reach && v <= hi + reach);
        if (!near.length) return r;
        return { ...r, priceRange: { minValue: Math.min(lo, ...near), maxValue: Math.max(hi, ...near) } };
      },
    });
    chart.current = c;
    series.current = s;
    markers.current = createSeriesMarkers(s, []);
    const bar = (d: unknown) => (d && typeof d === 'object' && 'high' in d ? (d as { open: number; high: number; low: number; close: number }) : null);
    // Pick mode: a click on a candle picks its start and the price under the pointer, inside its range.
    c.subscribeClick((p) => {
      const pk = pickRef.current;
      if (dragged.current) return void (dragged.current = false);
      const b = bar(p.seriesData.get(s));
      if (!pk || p.time == null || !p.point || !b) return;
      const y = s.coordinateToPrice(p.point.y);
      const price = Math.min(b.high, Math.max(b.low, y ?? b.close));
      pk.onPick(Number(p.time) * 1000, price.toFixed(dp), TF_SECONDS[cur.current.tf] / 60);
    });
    c.subscribeCrosshairMove((p) => {
      const b = pickRef.current ? bar(p.seriesData.get(s)) : null;
      setHover(b && p.time != null ? { t: Number(p.time) * 1000, o: b.open, h: b.high, l: b.low, c: b.close } : null);
    });
    // Pick mode: scrolling near either end loads the candles before (or, after Go to date, after) the ones shown.
    const step = (tf: Timeframe) => TF_SECONDS[tf] * 1000;
    const more = async (older: boolean) => {
      const e = edges.current;
      const have = bars.current;
      if (e.busy || !have.length) return;
      const { symbol: sym, tf: t } = cur.current;
      e.busy = true;
      try {
        const qs = older ? `&to=${have[0]!.t - 1}&limit=500` : `&from=${have[have.length - 1]!.t + 1}&to=${Math.min(Date.now(), have[have.length - 1]!.t + 500 * step(t))}&limit=500`;
        const rows = await api.get<Candle[]>(`/api/candles?symbol=${sym}&tf=${t}${qs}`);
        if (cur.current.symbol !== sym || cur.current.tf !== t || bars.current !== have || !series.current) return;
        if (older && !rows.length) e.older = false;
        if (!older) e.newer = rows.length > 0 && rows[rows.length - 1]!.t < Date.now() - 2 * step(t);
        if (!rows.length) return;
        const view = c.timeScale().getVisibleLogicalRange();
        bars.current = older ? [...rows, ...have] : [...have, ...rows];
        series.current.setData(bars.current.map((b) => ({ time: sec(b.t), open: +b.o, high: +b.h, low: +b.l, close: +b.c })));
        const shift = older ? rows.length : 0;
        if (view) c.timeScale().setVisibleLogicalRange({ from: view.from + shift, to: view.to + shift });
        setFirstBar(bars.current[0]!.t);
      } catch {
        /* keep what is shown */
      } finally {
        e.busy = false;
      }
    };
    c.timeScale().subscribeVisibleLogicalRangeChange((r) => {
      redraw();
      if (!pickRef.current || !r) return;
      if (r.from < 15 && edges.current.older) void more(true);
      else if (r.to > bars.current.length - 15 && edges.current.newer) void more(false);
    });
    c.timeScale().subscribeSizeChange(redraw);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
      c.remove();
      chart.current = series.current = markers.current = null;
      drawn.current = [];
    };
  }, [dp, live]);

  // Colours follow the theme.
  useEffect(() => {
    const up = cssVar('--green');
    const down = cssVar('--red');
    chart.current?.applyOptions({
      layout: { background: { type: ColorType.Solid, color: cssVar('--bg') }, textColor: cssVar('--muted'), fontFamily: 'Geist Mono, ui-monospace, monospace', fontSize: 10, attributionLogo: false },
      grid: { vertLines: { color: cssVar('--grid') }, horzLines: { color: cssVar('--grid') } },
      rightPriceScale: { borderColor: cssVar('--border') },
      timeScale: { borderColor: cssVar('--border') },
    });
    series.current?.applyOptions({ upColor: up, downColor: down, wickUpColor: up, wickDownColor: down, borderUpColor: up, borderDownColor: down });
  }, [theme, dp, live]);

  // History, then (live) the open candle from the feed.
  useEffect(() => {
    let gone = false;
    setLoaded('loading');
    // A trade's history shows the market around it too (at least 60 candles, or half its life, each
    // side), so a trade of a minute is not a chart of a minute. Live: the last 1,000 candles.
    const pad = range ? Math.max(TF_SECONDS[tf] * 1000 * 60, (range.to - range.from) / 2) : 0;
    // Pick mode, after Go to date or a typed entry time: 500 candles each side of that moment.
    const span = 500 * TF_SECONDS[tf] * 1000;
    const around = !range && focus != null ? { from: Math.floor(focus - span), to: Math.floor(Math.min(Date.now(), focus + span)) } : null;
    const qs = range
      ? `&from=${Math.floor(range.from - pad)}&to=${Math.floor(Math.min(Date.now(), range.to + pad))}&limit=1500`
      : around
        ? `&from=${around.from}&to=${around.to}&limit=1500`
        : '&limit=1000';
    bars.current = [];
    api
      .get<Candle[]>(`/api/candles?symbol=${symbol}&tf=${tf}${qs}`)
      .then((rows) => {
        if (gone || !series.current) return;
        // Short timeframes keep only recent history: when they don't reach back to the trade's open (or the
        // day gone to), step up to a longer one.
        const next = LIVE_TFS[LIVE_TFS.indexOf(tf) + 1];
        const reach = range?.from ?? around?.from ?? null;
        if (reach != null && auto.current && next && (!rows.length || rows[0]!.t > (range ? reach : focus!))) return setTf(next);
        bars.current = rows;
        edges.current = { older: true, newer: !!around && around.to < Date.now() - 2 * TF_SECONDS[tf] * 1000, busy: false };
        series.current.setData(rows.map((b) => ({ time: sec(b.t), open: +b.o, high: +b.h, low: +b.l, close: +b.c })));
        // Live: the last 150 candles in view, the rest a scroll / zoom away. History: the whole period.
        // Gone to a date: 150 candles centred on it.
        const mid = around ? rows.findIndex((b) => b.t >= focus!) : -1;
        if (around && rows.length) chart.current?.timeScale().setVisibleLogicalRange({ from: (mid < 0 ? rows.length : mid) - 75, to: (mid < 0 ? rows.length : mid) + 75 });
        else if (!range && rows.length > 150) chart.current?.timeScale().setVisibleLogicalRange({ from: rows.length - 150, to: rows.length + 3 });
        else chart.current?.timeScale().fitContent();
        setLoaded(rows.length ? 'ok' : 'empty');
        setFirstBar(rows[0]?.t ?? null);
        redraw();
        onCandles?.(range ? rows.filter((b) => b.t + TF_SECONDS[tf] * 1000 > range.from && b.t <= range.to) : rows);
      })
      .catch(() => !gone && setLoaded('empty'));
    return () => {
      gone = true;
    };
  }, [symbol, tf, range?.from, range?.to, dp, focus]); // eslint-disable-line react-hooks/exhaustive-deps
  useChannel<Candle>(live ? `candles:${symbol}:${tf}` : null, (b) => {
    // Candles that stop short of now (gone back to a date) take no live candle until the gap is loaded.
    const have = bars.current;
    if (edges.current.newer || !series.current || (have.length && b.t < have[have.length - 1]!.t)) return;
    series.current.update({ time: sec(b.t), open: +b.o, high: +b.h, low: +b.l, close: +b.c });
    if (have.length && have[have.length - 1]!.t === b.t) have[have.length - 1] = b;
    else have.push(b);
    if (loaded !== 'ok') setLoaded('ok');
  });
  // A typed entry time outside the candles shown: load the candles around it.
  useEffect(() => {
    const at = pick?.at;
    const have = bars.current;
    if (at == null || range || loaded === 'loading' || !have.length) return;
    if (at < have[0]!.t || at > have[have.length - 1]!.t + TF_SECONDS[tf] * 1000) setFocus(at);
  }, [pick?.at, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client online over the candles loaded (live: every 30 s, so a stretch going on grows).
  useEffect(() => {
    if (!accountId || firstBar == null || !showOnline) return;
    let gone = false;
    const load = () =>
      api
        .get<ClientOnline>(`/api/admin/desk/accounts/${accountId}/presence?from=${Math.max(firstBar, Date.now() - ONLINE_REACH_MS)}`)
        .then((r) => {
          if (gone) return;
          setOnline(r);
          redraw();
        })
        .catch(() => {});
    void load();
    const t = live ? setInterval(load, 30_000) : null;
    return () => {
      gone = true;
      if (t) clearInterval(t);
    };
  }, [accountId, firstBar, showOnline, live]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleOnline = () => {
    const next = !showOnline;
    setShowOnline(next);
    setTip(null);
    try {
      localStorage.setItem(ONLINE_PREF, next ? 'on' : 'off');
    } catch {
      /* remembered for this visit only */
    }
  };
  const visits = useMemo<Visit[]>(
    () => (online?.stretches ?? []).map((v) => ({ from: Date.parse(v.from), to: v.to == null ? null : Date.parse(v.to), pages: v.pages, device: v.device, who: v.who?.name })),
    [online],
  );
  const atMarks = useMemo(() => (online ? onlineAtMarks(marks, visits, Date.parse(online.historyFrom)) : []), [online, visits, marks]);

  // The lines: the trade's own, and the live Sell / Buy.
  const all = useMemo<DeskLine[]>(
    () => [
      ...lines,
      ...(live && quote
        ? [
            { id: 'bid', price: quote.bid.toString(), label: 'Sell', tone: 'down' as const },
            { id: 'ask', price: quote.ask.toString(), label: 'Buy', tone: 'up' as const },
          ]
        : []),
    ],
    [lines, live, quote],
  );
  useEffect(() => {
    const s = series.current;
    if (!s || drag.current) return;
    fit.current = lines.filter((l) => l.id !== 'stop' && l.price != null && D.from(l.price).isPos()).map((l) => Number(l.price));
    for (const d of drawn.current) s.removePriceLine(d.line);
    drawn.current = all
      .filter((l) => l.price != null && D.from(l.price).isPos())
      .map((l) => ({
        l,
        line: s.createPriceLine({
          price: Number(l.price),
          color: cssVar(TONE_VAR[l.tone]),
          lineWidth: l.id === 'bid' || l.id === 'ask' ? 1 : 2,
          lineStyle: l.id === 'bid' || l.id === 'ask' ? LineStyle.Dotted : l.dashed ? LineStyle.Dashed : LineStyle.Solid,
          axisLabelVisible: true,
          title: l.label,
        }),
      }));
  }, [all, theme]);

  useEffect(() => {
    if (!markers.current) return;
    const step = TF_SECONDS[tf];
    markers.current.setMarkers(
      marks
        .map((m) => ({
          time: (Math.floor(m.t / 1000 / step) * step) as UTCTimestamp,
          position: m.below ? ('belowBar' as const) : ('aboveBar' as const),
          shape: m.below ? ('arrowUp' as const) : ('arrowDown' as const),
          color: cssVar(TONE_VAR[m.tone]),
          text: m.label,
        }))
        .sort((a, b) => a.time - b.time),
    );
  }, [marks, tf, theme, loaded]);

  // Dragging TP / SL.
  const localY = (e: { clientY: number }) => e.clientY - (box.current?.getBoundingClientRect().top ?? 0);
  const lineAt = (y: number) => {
    let best: { id: string; d: number } | null = null;
    for (const d of drawn.current) {
      if (!d.l.drag) continue;
      const ly = series.current?.priceToCoordinate(Number(d.l.price));
      if (ly == null) continue;
      const dist = Math.abs(ly - y);
      if (dist <= 6 && (!best || dist < best.d)) best = { id: d.l.id, d: dist };
    }
    return best?.id ?? null;
  };
  const onDown = (e: ReactPointerEvent) => {
    if (!onDrag || e.button !== 0) return;
    const id = lineAt(localY(e));
    if (!id) return;
    e.preventDefault();
    chart.current?.applyOptions({ handleScroll: false, handleScale: false });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { id, moved: false };
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return setCursor(onDrag && lineAt(localY(e)) ? 'ns-resize' : '');
    const raw = series.current?.coordinateToPrice(localY(e));
    if (raw == null || raw <= 0) return;
    const price = raw.toFixed(dp);
    d.moved = true;
    drawn.current.find((x) => x.l.id === d.id)?.line.applyOptions({ price: Number(price) });
    onDrag?.(d.id, price);
  };
  const onUp = () => {
    if (!drag.current) return;
    dragged.current = drag.current.moved;
    drag.current = null;
    chart.current?.applyOptions({ handleScroll: true, handleScale: true });
  };

  // Where a moment sits on the time axis: between the candles loaded (weekend gaps are squeezed out of
  // the axis, so it is placed by candle, not by time), or past either end at one candle per step.
  const xOf = (t: number): number | null => {
    const b = bars.current;
    const ts = chart.current?.timeScale();
    if (!b.length || !ts) return null;
    const step = TF_SECONDS[tf] * 1000;
    let i: number;
    if (t < b[0]!.t) i = (t - b[0]!.t) / step;
    else {
      let lo = 0;
      let hi = b.length - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (b[mid]!.t <= t) lo = mid;
        else hi = mid - 1;
      }
      i = lo + Math.min(1, (t - b[lo]!.t) / step);
    }
    // The visible logical range spans the plot's width linearly (candle n is centred on logical n).
    const vr = ts.getVisibleLogicalRange();
    const w = ts.width();
    if (!vr || vr.to <= vr.from || !w) return null;
    return ((i - 0.5 - vr.from) / (vr.to - vr.from)) * w;
  };
  const plotW = chart.current ? chart.current.timeScale().width() : 0;
  const axisH = chart.current ? chart.current.timeScale().height() : 0;
  const drawOnline = !!accountId && showOnline && !!online && loaded === 'ok' && plotW > 0;
  const bands: { l: number; w: number; live: boolean }[] = [];
  const bubbles: { x: number; visits: Visit[]; text: string; left: number }[] = [];
  const dots: { x: number; at: number; device: string; who?: string }[] = [];
  const visitLen = (v: Visit) => (v.to ?? Date.now()) - v.from;
  if (drawOnline) {
    for (const v of visits) {
      const x0 = xOf(v.from);
      const x1 = v.to == null ? (xOf(Date.now()) ?? x0) : xOf(v.to);
      if (x0 == null || x1 == null || x1 < 0 || x0 > plotW) continue;
      const l = Math.max(0, x0);
      bands.push({ l, w: Math.max(2, Math.min(plotW, x1) - l), live: v.to == null });
      // Visits closer than a bubble's width share one bubble (a long timeframe, or zoomed out).
      const last = bubbles[bubbles.length - 1];
      if (last && x0 - last.x < 40) last.visits.push(v);
      else if (x0 >= 0) bubbles.push({ x: x0, visits: [v], text: '', left: x0 });
    }
    // Each bubble's label, and where it sits: at its visit, moved left to stay whole at the chart's right
    // edge (the latest visits are there) and clear of the bubble after it.
    let limit = plotW;
    for (let i = bubbles.length - 1; i >= 0; i--) {
      const g = bubbles[i]!;
      const total = g.visits.reduce((n, x) => n + visitLen(x), 0);
      g.text = g.visits.length > 1 ? `×${g.visits.length} · ${shortSpan(total)}` : g.visits[0]!.to == null ? 'Online now' : shortSpan(visitLen(g.visits[0]!));
      g.left = Math.max(0, Math.min(g.x, limit - (14 + g.text.length * 6.4)));
      limit = g.left - 3;
    }
    for (const e of online!.signIns) {
      const x = xOf(Date.parse(e.at));
      if (x != null && x >= 0 && x <= plotW) dots.push({ x, at: Date.parse(e.at), device: e.device, who: e.who?.name });
    }
  }

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-bg p-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[12px]">
        <span className="flex items-center gap-1.5 font-semibold">
          <InstrumentIcon symbol={symbol} size={18} />
          {symbol}
        </span>
        <span className="flex items-center gap-1.5">
          <span role="group" aria-label="Timeframe" className="inline-flex gap-0.5 rounded-md bg-raised p-0.5">
            {LIVE_TFS.map((x) => (
              <button key={x} type="button" aria-pressed={tf === x} onClick={() => pickTf(x)} className={cx('rounded px-2 py-0.5 text-[11px]', tf === x ? 'bg-panel font-semibold text-fg' : 'text-muted')}>
                {x === '1d' ? '1D' : x}
              </button>
            ))}
          </span>
          {accountId && (
            <button
              type="button"
              aria-pressed={showOnline}
              onClick={toggleOnline}
              title={followers ? 'When the master’s followers were online, each from their own device' : 'When the client themselves was online (not a team member logged in as them)'}
              className={cx('flex h-[22px] items-center gap-1.5 rounded-full border border-line px-2 text-[11px]', showOnline ? 'text-fg' : 'text-muted')}
            >
              <span className={cx('inline-block h-2 w-2 rounded-full', showOnline ? 'bg-up' : 'border border-muted')} aria-hidden="true" />
              {followers ? 'Followers online' : 'Client online'}
            </button>
          )}
          {!live && <span className="text-[11px] text-muted">price history</span>}
          {pick && !range && (
            <label className="flex items-center gap-1 text-[11px] text-muted">
              Go to date
              <input
                type="date"
                className="h-6 rounded-md border border-line bg-bg px-1 text-[11px] text-fg"
                max={new Date().toISOString().slice(0, 10)}
                aria-label="Go to date (UTC)"
                onChange={(e) => {
                  const t = Date.parse(`${e.target.value}T12:00:00Z`);
                  if (!Number.isNaN(t)) setFocus(Math.min(t, Date.now()));
                }}
              />
            </label>
          )}
        </span>
        {live && quote && (
          <span className="num flex items-center gap-1.5 text-[11px]">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-up motion-reduce:animate-none" aria-hidden="true" />
            Sell <b className="text-down">{D.from(quote.bid).toFixed(dp)}</b> · Buy <b className="text-up">{D.from(quote.ask).toFixed(dp)}</b>
          </span>
        )}
      </div>
      {pick && (
        <div className="num flex min-h-[18px] flex-wrap items-center gap-x-2 text-[11px] text-muted" aria-live="polite">
          {hover ? (
            <>
              <b className="font-medium text-fg">{utcLabel(hover.t)}</b>
              <span>{londonLabel(hover.t)} London</span>
              <span>
                O {hover.o.toFixed(dp)} H {hover.h.toFixed(dp)} L {hover.l.toFixed(dp)} C {hover.c.toFixed(dp)}
              </span>
            </>
          ) : (
            <span className="font-sans">Click a candle to pick its price and minute. Hover for its date, time and prices.</span>
          )}
        </div>
      )}
      <div className="relative" style={{ height }}>
        <div ref={box} className="absolute inset-0" style={{ cursor: cursor || (pick ? 'crosshair' : '') }} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} />
        {drawOnline && (
          <div className="pointer-events-none absolute top-0 left-0 z-10 overflow-hidden" style={{ width: plotW, bottom: axisH }} data-testid="client-online">
            {bands.map((b, i) => (
              <div key={`b${i}`} className="absolute top-0 bottom-0 border-l border-up/60 bg-up/10" style={{ left: b.l, width: b.w }} />
            ))}
            {dots.map((d, i) => (
              <span
                key={`d${i}`}
                className="pointer-events-auto absolute top-1.5 h-3 w-3 -translate-x-1/2 rounded-full border border-dashed border-muted bg-bg"
                style={{ left: d.x }}
                title={`${d.who ? `${d.who} signed in` : 'Signed in'} ${utcLabel(d.at)} (${londonLabel(d.at)} London) · ${d.device}. Online times are kept from ${DAY_FMT.format(Date.parse(online!.historyFrom))}.`}
              />
            ))}
            {bubbles.map((g, i) => {
              const isLive = g.visits.some((x) => x.to == null);
              return (
                <button
                  key={`v${i}`}
                  type="button"
                  aria-label={`${followers ? 'Followers' : 'Client'} online: ${g.visits.map((x) => `${x.who ? `${x.who}, ` : ''}${utcLabel(x.from)}, ${spanLabel(visitLen(x))}`).join('; ')}`}
                  onMouseEnter={() => setTip({ x: g.left, visits: g.visits })}
                  onMouseLeave={() => setTip(null)}
                  onFocus={() => setTip({ x: g.left, visits: g.visits })}
                  onBlur={() => setTip(null)}
                  className={cx(
                    'pointer-events-auto absolute top-1 h-[18px] rounded-full border border-up px-1.5 text-[10.5px] leading-none font-semibold whitespace-nowrap',
                    isLive ? 'animate-pulse bg-bg text-up motion-reduce:animate-none' : 'bg-up text-bg',
                  )}
                  style={{ left: g.left }}
                >
                  {g.text}
                </button>
              );
            })}
          </div>
        )}
        {drawOnline && tip && (
          <div
            role="tooltip"
            className="pointer-events-none absolute top-7 z-20 flex w-[260px] flex-col gap-2 rounded-lg border border-line-strong bg-raised p-2.5 text-[11.5px] shadow-lg"
            style={{ left: Math.max(0, Math.min(tip.x, plotW - 260)) }}
          >
            {tip.visits.slice(0, 4).map((v, i) => {
              const inside = marks.filter((m) => m.t >= v.from && (v.to == null || m.t <= v.to));
              return (
                <div key={i} className="flex flex-col gap-0.5">
                  <b className="flex items-center gap-1.5 text-[12px] font-semibold">
                    <span className="inline-block h-2 w-2 rounded-full bg-up" aria-hidden="true" />
                    {v.who ?? 'Client'} online{v.to == null ? ' now' : ''} · {spanLabel(visitLen(v))}
                  </b>
                  <span className="num text-muted">
                    {DAY_FMT.format(v.from)} {HM_FMT.format(v.from)} – {v.to == null ? 'now' : HM_FMT.format(v.to)} UTC ({londonLabel(v.from)}
                    {v.to == null ? '' : ` – ${londonLabel(v.to)}`})
                  </span>
                  {v.pages.length > 0 && <span>Pages: {v.pages.map((p) => PAGE[p] ?? p).join(', ')}</span>}
                  {v.device && <span className="text-muted">Last sign-in: {v.device}</span>}
                  {inside.map((m) => (
                    <span key={m.label + m.t} className="text-fg">
                      {m.label} at {HM_FMT.format(m.t)} during this visit
                    </span>
                  ))}
                </div>
              );
            })}
            {tip.visits.length > 4 && <span className="text-muted">and {tip.visits.length - 4} more; zoom in to see each.</span>}
          </div>
        )}
        {loaded !== 'ok' && <div className="pointer-events-none absolute inset-0 grid place-items-center text-xs text-muted">{loaded === 'loading' ? 'Loading prices…' : 'No price history for this period'}</div>}
      </div>
      {accountId && showOnline && online?.master?.followers === 0 && <span className="text-[11px] text-muted">No follower copied this master in the period shown.</span>}
      {accountId && showOnline && atMarks.length > 0 && !(online?.master?.followers === 0) && (
        <div className="flex flex-col gap-0.5 text-[11px]" data-testid="online-at-marks">
          {atMarks.map((m) => (
            <span key={m.label + m.t} className={m.state === 'online' ? 'text-up' : m.state === 'offline' ? 'text-warn' : 'text-muted'}>
              {m.label} {HM_FMT.format(m.t)} UTC:{' '}
              {m.state === 'online'
                ? online?.master
                  ? `${m.who.length > 1 ? `${m.who.length} followers were` : `${m.who[0] ?? 'a follower'} was`} online${m.who.length > 1 ? ` (${m.who.slice(0, 3).join(', ')}${m.who.length > 3 ? '…' : ''})` : ''}.`
                  : 'the client was online.'
                : m.state === 'offline'
                  ? online?.master
                    ? `no follower was online${m.ago != null ? `; the last one left ${spanLabel(m.ago)} before` : ''}.`
                    : `the client was offline${m.ago != null ? `; last online ${spanLabel(m.ago)} before` : ''}.`
                  : `before online times were kept (from ${DAY_FMT.format(Date.parse(online!.historyFrom))}).`}
            </span>
          ))}
        </div>
      )}
      <span className="text-[10.5px] text-muted">
        {pick ? 'Click a candle to pick its price and minute; drag its line to fine-tune the price. ' : ''}
        {onDrag ? 'Drag the TP or SL line to set it. ' : ''}Scroll or drag the chart to see earlier prices; mouse wheel to zoom.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- estimates

interface Row {
  label: string;
  price: D | null;
  usd: D | null;
  /** % of the margin; null hides it (e.g. the stop-out row shows the level instead) */
  pct?: D | null;
  note?: string;
  hl?: boolean;
}

function Kpi({ label, value, sub, cls }: { label: string; value: string; sub?: string; cls?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-px border-line px-3 py-2 not-last:border-r">
      <span className="text-[11px] text-muted">{label}</span>
      <span className={cx('num truncate text-[14px] font-medium', cls)}>{value}</span>
      {sub && <span className="num truncate text-[10.5px] text-muted">{sub}</span>}
    </div>
  );
}

function Rows({ head, rows, dp }: { head: string; rows: Row[]; dp: number }) {
  return (
    <table className="w-full border-collapse text-[12px]">
      <thead>
        <tr className="text-[11px] text-muted">
          <th className="border-t border-line px-3 py-1.5 text-left font-medium">{head}</th>
          <th className="border-t border-line px-3 py-1.5 text-right font-medium">Price</th>
          <th className="border-t border-line px-3 py-1.5 text-right font-medium">Profit / loss</th>
          <th className="border-t border-line px-3 py-1.5 text-right font-medium">On the margin</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className={r.hl ? 'bg-accent/10' : ''}>
            <td className="border-t border-line px-3 py-1.5 text-muted">{r.label}</td>
            <td className="num border-t border-line px-3 py-1.5 text-right">{r.price ? r.price.toFixed(dp) : '—'}</td>
            <td className={cx('num border-t border-line px-3 py-1.5 text-right whitespace-nowrap', tone(r.usd))}>{r.usd ? signedUsd(r.usd) : '—'}</td>
            <td className={cx('num border-t border-line px-3 py-1.5 text-right', r.note ? 'text-muted' : tone(r.pct ?? null))}>{r.note ?? signedPct(r.pct ?? null)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The account figures the stop-out estimate needs (Dealer Desk summary). */
export interface EstimateAccount {
  accountId: string;
  equity: string;
  marginUsed: string;
  /** stop-out margin level, e.g. "0.5" */
  stopOut: string;
}

const DEC = /^\d+(\.\d+)?$/;
const dec = (s: string | null | undefined) => (s && DEC.test(s.trim()) && D.from(s.trim()).isPos() ? D.from(s.trim()) : null);

/** Open position: the estimate before the trade is placed. */
export function OpenEstimate({
  inst,
  side,
  lots,
  leverage,
  quote,
  tp,
  sl,
  account,
  onStopOut,
  entry: entryAt = null,
}: {
  inst: Instrument;
  side: Side;
  lots: string;
  leverage: string;
  quote: LiveQuote | null;
  tp: string | null;
  sl: string | null;
  account: EstimateAccount | null;
  /** "From chart": the picked entry price in place of the live Sell / Buy. */
  entry?: string | null;
  /** The stop-out estimate, for the chart's line. */
  onStopOut?: (price: string | null) => void;
}) {
  const [what, setWhat] = useState('');
  const stopRef = useRef<string | null>(null);
  const report = (p: string | null) => {
    if (stopRef.current === p) return;
    stopRef.current = p;
    queueMicrotask(() => onStopOut?.(p));
  };
  const open = useLoad<{ data: { side: Side; qty: string; symbol: string }[] }>(account ? `/api/admin/desk/accounts/${account.accountId}/positions?status=open` : null, [account?.accountId]);
  const l = dec(lots);
  const lev = /^\d+$/.test(leverage) && Number(leverage) > 0 ? Number(leverage) : null;
  if (!quote || !l || !lev) {
    report(null);
    return <EstimateShell title="Estimate">Enter the amount and leverage to see the estimate.</EstimateShell>;
  }
  const dp = inst.priceDecimals;
  const pv = usdPerPoint(inst.contractSize, quote.fx);
  const qty = l.mul(inst.lotSize);
  const entry = dec(entryAt) ?? (side === 'long' ? D.from(quote.ask) : D.from(quote.bid));
  const notional = notionalOf(qty, entry, pv);
  const margin = initialMargin(notional, lev);
  const fee = openFeeFor(notional, inst.feeRate, inst.feeFlat);
  const backdated = !!dec(entryAt);
  // A chart entry fills at its own price, so no spread is paid now; its profit since the entry counts in the equity at once.
  const spread = backdated ? D.ZERO : D.from(quote.ask).sub(quote.bid).mul(qty).mul(pv);
  const pip = pipSize(dp).mul(qty).mul(pv);
  const at = (p: D) => pnl(side, entry, p, qty, pv);
  const sinceEntry = backdated ? at(D.from(side === 'long' ? quote.bid : quote.ask)) : D.ZERO;
  const up1 = entry.mul('1.01').round(dp);
  const dn1 = entry.mul('0.99').round(dp);
  // Stop-out with this trade added: the account's equity less the opening cost, its margin plus this one, and its net exposure on the instrument.
  let stop: D | null = null;
  if (account) {
    const legs = [...(open.data?.data ?? []).filter((p) => p.symbol === inst.symbol), { side, qty: qty.toString() }];
    stop = liquidationPrice(quote.mark, netUnits(legs, pv), D.from(account.equity).sub(fee).sub(spread).add(sinceEntry), D.from(account.marginUsed).add(margin), account.stopOut, dp);
  }
  report(stop?.toString() ?? null);
  const w = dec(what);
  const rows: Row[] = [
    ...(dec(tp) ? [{ label: 'Take profit', price: dec(tp), usd: at(dec(tp)!), pct: ofMargin(at(dec(tp)!), margin) }] : []),
    { label: side === 'long' ? '+1% move' : '−1% move', price: side === 'long' ? up1 : dn1, usd: at(side === 'long' ? up1 : dn1), pct: ofMargin(at(side === 'long' ? up1 : dn1), margin) },
    ...(dec(sl) ? [{ label: 'Stop loss', price: dec(sl), usd: at(dec(sl)!), pct: ofMargin(at(dec(sl)!), margin) }] : []),
    { label: side === 'long' ? '−1% move' : '+1% move', price: side === 'long' ? dn1 : up1, usd: at(side === 'long' ? dn1 : up1), pct: ofMargin(at(side === 'long' ? dn1 : up1), margin) },
    ...(stop ? [{ label: 'Account stopped out', price: stop, usd: at(stop), note: `at ${D.from(account!.stopOut).mul(100).toFixed(0)}% margin level`, hl: true }] : []),
  ];
  const free = account ? D.from(account.equity).sub(account.marginUsed) : null;
  return (
    <EstimateShell title={`Estimate for ${side === 'long' ? 'Buy' : 'Sell'} ${l.toString()} lots`} right={`at ${entry.toFixed(dp)} · 1:${lev}`}>
      <div className="grid grid-cols-2">
        <Kpi label="Position value" value={fmtUsd(notional, 0)} sub="notional" />
        <Kpi label="Margin needed" value={fmtUsd(margin)} sub={free && free.isPos() ? `${margin.div(free).mul(100).toFixed(0)}% of free margin` : undefined} />
        <Kpi label="1 pip is worth" value={fmtUsd(pip)} sub={`1% move = ${fmtUsd(notional.div(100))}`} />
        <Kpi label="Opening cost" value={fmtUsd(spread.add(fee))} sub={`${fmtUsd(spread)} spread + ${fmtUsd(fee)} fee`} />
      </div>
      <Rows head="If the price reaches" rows={rows} dp={dp} />
      <div className="grid grid-cols-[auto_120px_minmax(0,1fr)] items-center gap-2 border-t border-line px-3 py-2 text-[12px]">
        <label htmlFor="desk-whatif" className="text-muted">
          Profit if price is
        </label>
        <input id="desk-whatif" className="num h-8 rounded-md border border-line bg-bg px-2 text-[12px] text-fg outline-none focus:border-line-strong" value={what} placeholder={(side === 'long' ? up1 : dn1).toFixed(dp)} onChange={(e) => setWhat(e.target.value)} />
        <span className={cx('num', w ? tone(at(w)) : 'text-muted')}>{w ? `${signedUsd(at(w))} · ${signedPct(ofMargin(at(w), margin))} on the margin` : 'Type a price to answer a “what if”.'}</span>
      </div>
    </EstimateShell>
  );
}

export interface EstimatePos {
  side: Side;
  qty: string;
  entryPrice: string;
  leverage: number;
  margin?: string;
  swap: string;
  openFee: string;
  closeFee: string | null;
  tpPrice: string | null;
  slPrice: string | null;
  liqPrice?: string | null;
  closePrice: string | null;
  realizedPnl: string | null;
  openedAt: string;
  closedAt: string | null;
}

/**
 * The trade in plain words, from the same figures the estimates use: what it is, what it costs in
 * margin, what a pip and a 1% move are worth. `entry` is the open price (an existing trade) or the
 * live price (a new one); `free` the account's free margin; `stopOut` the estimated stop-out price.
 */
export function PlainWords({ inst, side, lots, leverage, entry, quote, free, stopOut, extra }: { inst: Instrument; side: Side; lots: string; leverage: string; entry: string | null; quote: LiveQuote | null; free: string | null; stopOut: string | null; extra?: string[] }) {
  const l = dec(lots);
  const lev = /^\d+$/.test(leverage) && Number(leverage) > 0 ? Number(leverage) : null;
  const price = dec(entry) ?? (quote ? (side === 'long' ? D.from(quote.ask) : D.from(quote.bid)) : null);
  const shell = (text: ReactNode) => (
    <div className="flex flex-col gap-1 rounded-lg border border-accent/60 bg-accent/10 px-3 py-2 text-[12.5px] text-fg">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-accent">In plain words</span>
      {text}
    </div>
  );
  if (!l || !lev || !price) return shell(<span className="text-muted">Enter the amount and leverage to see what this trade is.</span>);
  const dp = inst.priceDecimals;
  const pv = usdPerPoint(inst.contractSize, quote?.fx);
  const qty = l.mul(inst.lotSize);
  const notional = notionalOf(qty, price, pv);
  const margin = initialMargin(notional, lev);
  const pip = pipSize(dp).mul(qty).mul(pv);
  const freeD = free ? D.from(free) : null;
  const pct = freeD && freeD.isPos() ? `, ${margin.div(freeD).mul(100).toFixed(0)}% of free margin` : '';
  const stop = dec(stopOut);
  const stopUsd = stop ? pnl(side, price, stop, qty, pv) : null;
  return shell(
    <>
      <span>
        <b>
          <span className={side === 'long' ? 'text-up' : 'text-down'}>{side === 'long' ? 'Buy' : 'Sell'}</span> {l.toString()} lots <InstrumentLabel symbol={inst.symbol} size={14} className="gap-1" />
        </b>{' '}
        = {Number(qty.toString()).toLocaleString('en-US', { maximumFractionDigits: 8 })} {inst.unit}, a <b className="num">{fmtUsd(notional, 0)}</b> position on <b className="num">{fmtUsd(margin)}</b> margin (1:{lev}
        {pct}).
      </span>
      <span className="num flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-muted">
        <span>
          1 pip = <b className="text-fg">{fmtUsd(pip)}</b>
        </span>
        <span>
          1% move = <b className="text-fg">{fmtUsd(notional.div(100))}</b>
        </span>
        {stop && (
          <span>
            Stop-out near <b className="text-fg">{stop.toFixed(dp)}</b>
            {stopUsd ? ` (${signedUsd(stopUsd)}, account-wide)` : ''}
          </span>
        )}
        {extra?.map((x) => <span key={x}>{x}</span>)}
      </span>
    </>,
  );
}

// ---------------------------------------------------------------- leverage (MASTER_EDITS_PLAN.md)

/** The presets (Leon, 2026-10-20: four and Custom); only those the trade may use are shown. */
const LEV_PRESETS = [10, 20, 50, 100];

export interface LeverageRange {
  /** The instrument's own range. */
  min: number;
  max: number;
  /** The client's max leverage, or the master's cap; null when there is none. */
  cap: number | null;
  /** Whose cap: "this client's", "Gold Desk's". */
  capOf?: string;
  /** The trade's leverage as booked (Edit): kept even above a cap set later. */
  now?: number;
}

/** The highest leverage that may be picked: the instrument's maximum, or the cap below it. */
export const leverageTop = (r: LeverageRange) => (r.cap != null ? Math.max(r.min, Math.min(r.max, r.cap)) : r.max);

/** What is wrong with a typed leverage, in words; null when it may be saved. The trade's own (`now`) always may. */
export function leverageProblem(value: string, r: LeverageRange): string | null {
  const v = value.trim();
  if (!/^\d{1,4}$/.test(v) || Number(v) < 1) return 'Type a whole number, e.g. 20 for 1:20.';
  const n = Number(v);
  if (n === r.now) return null;
  if (n < r.min) return `1:${n} is below the lowest leverage for this instrument, 1:${r.min}.`;
  if (n > r.max) return `1:${n} is above the highest leverage for this instrument, 1:${r.max}.`;
  if (r.cap != null && n > r.cap) return `1:${n} is above ${r.capOf ?? 'the'} cap of 1:${r.cap}. Pick 1:${r.min} – 1:${leverageTop(r)}.`;
  return null;
}

/**
 * Leverage for the desk pop-ups: one-click presets (1:10, 1:20, 1:50, 1:100, those within the
 * instrument's range and the cap), and Custom, which opens a box to step with − / + (or ↑ ↓) or type any
 * whole number in range. A leverage that is not a preset shows on the Custom chip. Under it the margin
 * (before → after in Edit) and the allowed range with its cap. `problem` adds the caller's own (not enough
 * free margin). `defaultLev` marks the instrument's default (Open); `range.now` the trade's own (Edit).
 */
export function LeverageField({
  value,
  onChange,
  range,
  defaultLev,
  margin,
  problem,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  range: LeverageRange;
  defaultLev?: number;
  /** Margin with the trade's leverage as booked (Edit; none on a new trade) and with the typed one. */
  margin?: { before: D | null; after: D | null };
  problem?: string | null;
  disabled?: boolean;
}) {
  const top = leverageTop(range);
  const n = /^\d{1,4}$/.test(value.trim()) ? Number(value.trim()) : null;
  const wrong = leverageProblem(value, range) ?? problem ?? null;
  const presets = LEV_PRESETS.filter((x) => x >= range.min && x <= top);
  const [picked, setPicked] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  // Custom is open when chosen, and whenever the leverage is not a preset (typed, or booked as it is).
  const custom = picked || n == null || !presets.includes(n);
  const step = (d: number) => onChange(String(Math.min(range.max, Math.max(range.min, (n ?? range.now ?? defaultLev ?? range.min) + d))));
  const openCustom = () => {
    setPicked(true);
    requestAnimationFrame(() => box.current?.select());
  };
  const diff = margin?.before && margin.after ? margin.after.sub(margin.before) : null;
  const chip = (on: boolean) => cx('num flex h-8 items-center gap-1 rounded-lg border px-2.5 text-[12px]', on ? 'border-accent bg-accent/10 font-semibold text-accent' : 'border-line text-fg hover:border-line-strong');
  const tag = 'font-sans text-[9.5px] font-semibold tracking-wide text-muted uppercase';
  const mark = (x: number) => (x === range.now ? 'now' : range.now == null && x === defaultLev ? 'default' : null);
  const stepBtn = 'grid h-9 w-8 place-items-center bg-raised/60 text-[15px] text-muted hover:text-fg disabled:opacity-40';
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div role="group" aria-label="Leverage" className="flex flex-wrap gap-1">
        {presets.map((x) => (
          <button
            key={x}
            type="button"
            disabled={disabled}
            aria-pressed={!custom && n === x}
            onClick={() => {
              setPicked(false);
              onChange(String(x));
            }}
            className={chip(!custom && n === x)}
          >
            1:{x}
            {mark(x) && <span className={tag}>{mark(x)}</span>}
          </button>
        ))}
        <button type="button" disabled={disabled} aria-pressed={custom} onClick={openCustom} className={cx(chip(custom), 'font-sans')}>
          {custom && n != null ? (
            <>
              Custom <span className="num">1:{n}</span>
              {mark(n) && <span className={tag}>{mark(n)}</span>}
            </>
          ) : (
            'Custom…'
          )}
        </button>
      </div>
      {custom && (
        <div className={cx('flex h-9 items-stretch self-start overflow-hidden rounded-lg border bg-bg', wrong ? 'border-down' : 'border-accent')}>
          <button type="button" className={stepBtn} disabled={disabled || (n ?? 0) <= range.min} onClick={() => step(-1)} aria-label="Lower leverage">
            −
          </button>
          <span className="num flex items-center pl-2 text-[13px] text-muted">1:</span>
          <input
            ref={box}
            className="num w-12 bg-transparent pr-1 text-[13px] text-fg outline-none"
            value={value}
            disabled={disabled}
            inputMode="numeric"
            onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                step(e.key === 'ArrowUp' ? 1 : -1);
              }
            }}
            aria-label="Custom leverage"
            aria-invalid={!!wrong}
          />
          <button type="button" className={stepBtn} disabled={disabled || (n ?? 0) >= range.max} onClick={() => step(1)} aria-label="Raise leverage">
            +
          </button>
        </div>
      )}
      <span className="num flex flex-wrap gap-x-3 text-[11px] text-muted">
        {margin?.after && (
          <span>
            Margin {margin.before && diff && !diff.isZero() ? <>{fmtUsd(margin.before)} → </> : null}
            <b className="text-fg">{fmtUsd(margin.after)}</b>
            {diff && !diff.isZero() && <span className={diff.isPos() ? 'text-down' : 'text-up'}> ({signedUsd(diff)})</span>}
          </span>
        )}
        <span>
          {custom ? `Type 1:${range.min} – 1:${top} or use − / +` : `Allowed 1:${range.min} – 1:${top}`}
          {range.cap != null && top < range.max ? ` (${range.capOf ?? 'the'} cap)` : ''}
        </span>
      </span>
      {wrong && <span className="rounded-md bg-down/10 px-2 py-1 text-[11.5px] text-down">{wrong}</span>}
    </div>
  );
}

/** Edit open position: how the position stands now, live. */
export function OpenPositionNow({ pos, inst, quote, tp, sl }: { pos: EstimatePos; inst: Instrument; quote: LiveQuote | null; tp: string | null; sl: string | null }) {
  if (!quote) return <EstimateShell title="Position now">Waiting for a live price…</EstimateShell>;
  const dp = inst.priceDecimals;
  const pv = usdPerPoint(inst.contractSize, quote.fx);
  const qty = D.from(pos.qty);
  const rate = exitPrice(pos.side, { bid: D.from(quote.bid), ask: D.from(quote.ask) });
  const at = (p: D) => pnl(pos.side, pos.entryPrice, p, qty, pv);
  const margin = pos.margin ? D.from(pos.margin) : initialMargin(notionalOf(qty, pos.entryPrice, pv), pos.leverage);
  const now = at(rate);
  const closeFee = feeFor(notionalOf(qty, rate, pv), inst.feeRate);
  const net = now.add(pos.swap).sub(closeFee);
  const pip = pipSize(dp).mul(qty).mul(pv);
  const pips = (p: D | null) => (p ? `${p.sub(rate).abs().div(pipSize(dp)).toFixed(1)} pips` : '—');
  const tpD = dec(tp);
  const slD = dec(sl);
  const liq = dec(pos.liqPrice);
  const rows: Row[] = [
    ...(tpD ? [{ label: 'Take profit', price: tpD, usd: at(tpD), pct: ofMargin(at(tpD), margin) }] : []),
    ...(slD ? [{ label: 'Stop loss', price: slD, usd: at(slD), pct: ofMargin(at(slD), margin) }] : []),
    ...(liq ? [{ label: 'Account stopped out', price: liq, usd: at(liq), note: 'account-wide estimate', hl: true }] : []),
  ];
  return (
    <EstimateShell title={`Position now · ${qty.div(inst.lotSize).toString()} lots`} right={`margin ${fmtUsd(margin)} · 1:${pos.leverage} · live`}>
      <div className="grid grid-cols-2">
        <Kpi label="Profit now" value={signedUsd(now)} cls={tone(now)} sub={`close rate ${rate.toFixed(dp)}`} />
        <Kpi label="After swap and fees" value={signedUsd(net)} cls={tone(net)} sub="if closed now" />
        <Kpi label="On the margin" value={signedPct(ofMargin(now, margin))} cls={tone(now)} sub={`margin ${fmtUsd(margin)}`} />
        <Kpi label="1 pip is worth" value={fmtUsd(pip)} sub={`1% move = ${fmtUsd(notionalOf(qty, rate, pv).div(100))}`} />
      </div>
      {rows.length > 0 && <Rows head="If it closes at" rows={rows} dp={dp} />}
      <div className="grid grid-cols-3 gap-2 border-t border-line px-3 py-2 text-[11px]">
        <span>
          <span className="text-muted">to TP </span>
          <span className="num text-up">{pips(tpD)}</span>
        </span>
        <span>
          <span className="text-muted">to SL </span>
          <span className="num text-down">{pips(slD)}</span>
        </span>
        <span>
          <span className="text-muted">to stop-out </span>
          <span className="num text-warn">{pips(liq)}</span>
        </span>
      </div>
    </EstimateShell>
  );
}

/** Edit closed position: what the trade made, and its best and worst point from our candles. */
export function ClosedResult({ pos, inst, candles, booked }: { pos: EstimatePos; inst: Instrument; candles: Candle[] | null; /** The trade as booked, when `pos` carries typed figures: the result follows the typed ones. */ booked?: ClosedBooked }) {
  const dp = inst.priceDecimals;
  const swap = D.from(pos.swap);
  const fees = D.from(pos.openFee).add(pos.closeFee ?? 0);
  const perPoint = closedPerPoint(pos, inst, booked);
  const at = (p: D) => p.sub(pos.entryPrice).mul(pos.side === 'long' ? 1 : -1).mul(perPoint);
  const gross = pos.closePrice ? at(D.from(pos.closePrice)) : D.from(pos.realizedPnl ?? 0).sub(swap);
  const net = gross.add(swap).sub(fees);
  const margin = pos.margin ? D.from(pos.margin) : initialMargin(D.from(pos.entryPrice).mul(perPoint), pos.leverage);
  let best: D | null = null;
  let worst: D | null = null;
  if (candles && candles.length) {
    const hi = candles.reduce((m, c) => (D.from(c.h).gt(m) ? D.from(c.h) : m), D.from(candles[0]!.h));
    const lo = candles.reduce((m, c) => (D.from(c.l).lt(m) ? D.from(c.l) : m), D.from(candles[0]!.l));
    [best, worst] = pos.side === 'long' ? [hi, lo] : [lo, hi];
  }
  const held = pos.closedAt ? (Date.parse(pos.closedAt) - Date.parse(pos.openedAt)) / 3_600_000 : 0;
  return (
    <EstimateShell title="Result" right={`held ${held >= 24 ? `${Math.floor(held / 24)} d ${Math.round(held % 24)} h` : `${held.toFixed(1)} h`} · 1:${pos.leverage}`}>
      <div className="grid grid-cols-2">
        <Kpi label="Profit" value={signedUsd(gross)} cls={tone(gross)} sub="before swap and fees" />
        <Kpi label="Swap · fees" value={signedUsd(swap.sub(fees))} cls={tone(swap.sub(fees))} sub={`${signedUsd(swap)} · ${signedUsd(fees.neg())}`} />
        <Kpi label="Net" value={signedUsd(net)} cls={tone(net)} sub="to the balance" />
        <Kpi label="On the margin" value={signedPct(ofMargin(net, margin))} cls={tone(net)} sub={`margin ${fmtUsd(margin)}`} />
      </div>
      <Rows
        head="During the trade"
        dp={dp}
        rows={[
          { label: 'Best point', price: best, usd: best ? at(best) : null, pct: best ? ofMargin(at(best), margin) : null, ...(candles && !candles.length ? { note: 'no price history' } : {}) },
          { label: 'Worst point', price: worst, usd: worst ? at(worst) : null, pct: worst ? ofMargin(at(worst), margin) : null, ...(candles && !candles.length ? { note: 'no price history' } : {}) },
        ]}
      />
    </EstimateShell>
  );
}

/** A closed trade's figures as booked, when the pop-up's `pos` carries typed ones. */
export type ClosedBooked = { entryPrice: string; closePrice: string | null; side?: Side; qty?: string };

/**
 * A closed trade's USD per 1.0 of price for the whole position, from its own booked result (which holds
 * whatever the fx was at the close), so typed prices are valued the way the trade was. A typed amount
 * takes the same value per unit.
 */
export function closedPerPoint(pos: EstimatePos, inst: Instrument, booked?: ClosedBooked) {
  const b = { side: pos.side, qty: pos.qty, ...(booked ?? { entryPrice: pos.entryPrice, closePrice: pos.closePrice }) };
  const gross = D.from(pos.realizedPnl ?? 0).sub(pos.swap);
  const move = b.closePrice ? D.from(b.closePrice).sub(b.entryPrice) : D.ZERO;
  const per = !move.isZero() && !gross.isZero() ? gross.div(move.mul(b.side === 'long' ? 1 : -1)) : D.from(b.qty).mul(usdPerPoint(inst.contractSize));
  return D.from(pos.qty).eq(b.qty) ? per : per.mul(pos.qty).div(b.qty);
}

/** A round step of about 0.1% of the price (1, 2 or 5 × a power of ten), never below one price unit. */
function rateStep(price: D, dp: number): D {
  const raw = Number(price.toString()) / 1000;
  const unit = 10 ** -dp;
  if (!(raw > unit)) return D.from(unit.toFixed(dp));
  const p = 10 ** Math.floor(Math.log10(raw));
  const m = raw / p;
  return D.from(((m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10) * p).toFixed(dp));
}

/**
 * The client's profit or loss for an open rate: the original rate against the one typed or picked, a
 * ladder of rates around it, and any rate typed in. Profit is at `exit`: the live close rate for an
 * open trade, the close price for a closed one. `perPoint`: USD per 1.0 of price for the whole position.
 */
export function OpenRateImpact({
  inst,
  side,
  perPoint,
  exit,
  exitLabel,
  original,
  typed,
  originalLabel = 'Original open rate',
  master = false,
}: {
  inst: Instrument;
  side: Side;
  perPoint: D;
  exit: D | null;
  exitLabel: string;
  original: D | null;
  typed: D | null;
  originalLabel?: string;
  /** A master account's trade: the master's result, not a client's. */
  master?: boolean;
}) {
  const [what, setWhat] = useState('');
  const id = useId();
  const dp = inst.priceDecimals;
  const base = typed ?? original;
  const title = `${master ? 'Master' : 'Client'}'s profit / loss by open rate`;
  if (!exit || !base) return <EstimateShell title={title}>Waiting for a price…</EstimateShell>;
  const at = (open: D) => exit.sub(open).mul(side === 'long' ? 1 : -1).mul(perPoint);
  const step = rateStep(base, dp);
  const pips = (d: D) => `${d.isNeg() ? '−' : '+'}${d.abs().div(pipSize(dp)).toFixed(1)} pips`;
  const changed = original && typed && !typed.eq(original);
  const rows = [-3, -2, -1, 0, 1, 2, 3].map((k) => base.add(step.mul(k)).round(dp)).filter((r) => r.isPos());
  const w = dec(what);
  return (
    <EstimateShell title={title} right={`${exitLabel} ${exit.toFixed(dp)}`}>
      {changed && (
        <div className="flex flex-wrap items-baseline gap-x-2 border-t border-line bg-accent/10 px-3 py-2 text-[12.5px]">
          <span className="text-muted">Open rate</span>
          <s className="num text-muted">{original!.toFixed(dp)}</s>→<b className="num">{typed!.toFixed(dp)}</b>
          <span className="text-muted">· profit</span>
          <s className={cx('num', tone(at(original!)))}>{signedUsd(at(original!))}</s>→<b className={cx('num', tone(at(typed!)))}>{signedUsd(at(typed!))}</b>
          <span className={cx('num font-semibold', tone(at(typed!).sub(at(original!))))}>({signedUsd(at(typed!).sub(at(original!)))} for the client)</span>
        </div>
      )}
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="text-[11px] text-muted">
            <th className="border-t border-line px-3 py-1.5 text-left font-medium">Open rate</th>
            <th className="border-t border-line px-3 py-1.5 text-right font-medium">Profit / loss</th>
            <th className="border-t border-line px-3 py-1.5 text-right font-medium">{original ? 'Against the original' : 'Move'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isTyped = !!typed && r.eq(typed.round(dp));
            const isOrig = !!original && r.eq(original.round(dp));
            return (
              <tr key={r.toString()} className={isTyped ? 'bg-accent/10' : ''}>
                <td className="num border-t border-line px-3 py-1.5">
                  {r.toFixed(dp)}
                  {isTyped && <span className="ml-1.5 font-sans text-[10.5px] text-accent">{changed ? 'new' : 'this trade'}</span>}
                  {isOrig && !isTyped && <span className="ml-1.5 font-sans text-[10.5px] text-muted">{originalLabel.toLowerCase()}</span>}
                </td>
                <td className={cx('num border-t border-line px-3 py-1.5 text-right', tone(at(r)))}>{signedUsd(at(r))}</td>
                <td className="num border-t border-line px-3 py-1.5 text-right text-muted">{original ? signedUsd(at(r).sub(at(original))) : pips(r.sub(base))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="grid grid-cols-[auto_120px_minmax(0,1fr)] items-center gap-2 border-t border-line px-3 py-2 text-[12px]">
        <label htmlFor={id} className="text-muted">
          Profit if opened at
        </label>
        <input id={id} className="num h-8 rounded-md border border-line bg-bg px-2 text-[12px] text-fg outline-none focus:border-line-strong" value={what} placeholder={base.toFixed(dp)} onChange={(e) => setWhat(e.target.value)} />
        <span className={cx('num', w ? tone(at(w)) : 'text-muted')}>
          {w ? `${signedUsd(at(w))}${original ? ` · ${signedUsd(at(w).sub(at(original)))} against the original` : ''}` : `Type an open rate to see the ${master ? 'master' : 'client'}’s result.`}
        </span>
      </div>
    </EstimateShell>
  );
}

function EstimateShell({ title, right, children }: { title: string; right?: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="rounded-lg border border-line bg-bg">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-[12px]">
        <span className="font-semibold">{title}</span>
        {right && <span className="num text-[11px] text-muted">{right}</span>}
      </div>
      {typeof children === 'string' ? <p className="m-0 border-t border-line px-3 py-3 text-[12px] text-muted">{children}</p> : children}
    </section>
  );
}
